import {
  Badge,
  Box,
  Center,
  Flex,
  Spinner,
  Text,
  useColorModeValue,
  useTheme,
} from "@chakra-ui/react";
import { Anchor } from "mapbox-gl";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BackButton from "../../components/BackButton";
import BusArrivalTime from "../../components/buses/BusArrivalTime";
import FavouriteButton from "../../components/FavouriteButton";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import { MapPin } from "../../components/Map";
import QueryStateWrapper from "../../components/QueryStateWrapper";
import {
  BusLocation,
  createBusSVG,
  getBusStop,
  useBusRoute,
  useBusTimes,
} from "../../data/buses.api";
import { useFocusOnPoints } from "../../hooks/useFocusOnPoints";

const BusRoute = () => {
  const { search } = useLocation();
  const theme = useTheme();
  const { atcoCode } = useParams<{ atcoCode: string }>();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const borderColour = useColorModeValue("blackAlpha.800", "whiteAlpha.500");
  const blockColour = useColorModeValue("lancsBlue.500", "lancsBlue.300");
  const blockText = useColorModeValue("white", "black");
  const { data, isLoading, error } = useBusRoute({
    atcoCode,
    aimedDepartureTime: searchParams.get("aimedDepartureTime") ?? undefined,
    operator: searchParams.get("operator") ?? undefined,
    line: searchParams.get("line") ?? undefined,
    dir: searchParams.get("dir") ?? undefined,
  });
  const navigate = useNavigate();
  const { data: buses } = useBusTimes(atcoCode);
  const [busStopLocations, setBusStopLocations] = useState<MapPin[]>([]);
  const [busLocations, setBusLocations] = useState<MapPin[]>([]);
  const allPins = useMemo(() => {
    return [...busStopLocations, ...busLocations];
  }, [busLocations, busStopLocations]);
  useFocusOnPoints(allPins);

  useEffect(() => {
    if (buses) {
      setBusLocations(
        buses
          .filter((time) => time.line === searchParams.get("line"))
          .filter((busTime) => busTime.vehicleLocation !== null)
          .map((busTime) => ({
            anchor: "top",
            shake: "circle",
            size: 24,
            imgUrl: createBusSVG(
              busTime.line,
              busTime.vehicleLocation?.bearing
                ? parseInt(busTime.vehicleLocation.bearing)
                : null
            ),
            imgScale: 5,
            color: "transparent",
            lng: busTime.vehicleLocation!.longitude,
            lat: busTime.vehicleLocation!.latitude,
          }))
      );
    }
  }, [buses, searchParams, theme]);

  useEffect(() => {
    if (data) {
      Promise.all(data.map((busTime) => getBusStop(busTime.atcocode))).then(
        (busStops) => {
          setBusStopLocations([
            ...busStops.map((stop) => ({
              lng: parseFloat(stop.longitude),
              lat: parseFloat(stop.latitude),
              size: 30,
              onClick: () => navigate(`/buses/${stop.atcoCode}`),
            })),
          ]);
        }
      );
    }
  }, [data, navigate]);

  return (
    <HybridBottomSheetDrawer>
      <BackButton />
      <Box p={8}>
        <Flex justify="space-between">
          <Flex alignItems="center" gap={3} fontSize="xl" mb={4}>
            <Text my={3}>
              <Badge fontSize="0.8em" colorScheme="blue">
                {searchParams.get("line")}
              </Badge>
            </Text>
            <Text>{searchParams.get("dest")}</Text>
          </Flex>
          <FavouriteButton name={`${searchParams.get("line")} Route`} />
        </Flex>
        <QueryStateWrapper isLoading={isLoading} error={error} data={data}>
          <Flex
            direction="column"
            alignItems="start"
            data-testid="busRoute"
            ml={"15px"}
            w="full"
            gap={10}
            borderLeft={data ? "1px solid" : undefined}
            borderColor={borderColour}
          >
            {data
              ? data.map((busTime, i) => (
                  <Flex gap={5} ml={"-15px"} alignItems="center" w="full">
                    <Center
                      color={blockText}
                      fontWeight="bold"
                      bg={blockColour}
                      rounded="full"
                      w={"30px"}
                      h={"30px"}
                    >
                      {i + 1}
                    </Center>
                    <Box flex={1} pr={3}>
                      {busTime.directionLocality}, {busTime.destinationName}
                    </Box>
                    <BusArrivalTime
                      busTime={busTime}
                      showScheduledTime
                      arrivalTime={true}
                    />
                  </Flex>
                ))
              : null}
          </Flex>
        </QueryStateWrapper>
      </Box>
    </HybridBottomSheetDrawer>
  );
};

export default BusRoute;
